/**
 * Design Tokens: Liquid Glass
 * Apple's Liquid Glass design principles for modern, adaptive interfaces
 * 
 * Based on Apple's WWDC 2025 Liquid Glass design system:
 * - Real-time depth and transparency
 * - Motion-responsive effects
 * - Physically accurate lensing and refraction
 * - Accessibility-first approach
 */

import { type ColorTheme } from './colors'

// Core Liquid Glass variants
export type LiquidGlassVariant = 
  | 'regular'      // Standard glass effect for most UI elements
  | 'clear'        // Minimal glass for content overlays
  | 'prominent'    // Enhanced glass for navigation and key elements
  | 'ultraThin'    // Subtle glass for secondary elements

// Layer depth system for spatial hierarchy
export type LiquidGlassLayer = 
  | 'background'   // Base layer (-1)
  | 'content'      // Content layer (0)
  | 'surface'      // Surface elements (1)
  | 'elevated'     // Elevated elements (2)
  | 'modal'        // Modal and overlay elements (3)
  | 'tooltip'      // Tooltips and floating elements (4)

// Motion preference for accessibility
export type MotionPreference = 'full' | 'reduced' | 'none'

export const liquidGlass = {
  // Core transparency values — dark mode is fully opaque (flat surfaces)
  transparency: {
    regular: {
      light: 0.8,    // 80% opacity for light mode
      dark: 1,       // Fully opaque — no glass in dark mode
    },
    clear: {
      light: 0.95,   // 95% opacity for minimal interference
      dark: 1,       // Fully opaque
    },
    prominent: {
      light: 0.7,    // 70% opacity for stronger presence
      dark: 1,       // Fully opaque
    },
    ultraThin: {
      light: 0.92,   // 92% opacity for subtle effects
      dark: 1,       // Fully opaque
    },
  },

  // Blur intensity values — no blur in dark mode (flat, clean)
  blur: {
    regular: {
      light: 20,     // Standard blur for light backgrounds
      dark: 0,       // No blur in dark mode
    },
    clear: {
      light: 8,      // Minimal blur for content clarity
      dark: 0,       // No blur
    },
    prominent: {
      light: 28,     // Strong blur for emphasis
      dark: 0,       // No blur
    },
    ultraThin: {
      light: 12,     // Subtle blur effect
      dark: 0,       // No blur
    },
  },

  // Depth layers with z-index and shadow values
  depth: {
    background: {
      zIndex: -1,
      shadow: 'none',
    },
    content: {
      zIndex: 0,
      shadow: 'none',
    },
    surface: {
      zIndex: 1,
      shadow: {
        light: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        dark: '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2)',
      },
    },
    elevated: {
      zIndex: 10,
      shadow: {
        light: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
        dark: '0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.25)',
      },
    },
    modal: {
      zIndex: 1000,
      shadow: {
        light: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
        dark: '0 10px 15px rgba(0, 0, 0, 0.5), 0 4px 6px rgba(0, 0, 0, 0.3)',
      },
    },
    tooltip: {
      zIndex: 1500,
      shadow: {
        light: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
        dark: '0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.25)',
      },
    },
  },

  // Motion and interaction effects
  motion: {
    // Transition durations
    duration: {
      instant: '0ms',
      fast: '150ms',
      normal: '250ms',
      slow: '350ms',
      slower: '500ms',
    },
    
    // Easing curves for natural motion
    easing: {
      // Apple's signature easing curves
      standard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
      decelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
      accelerate: 'cubic-bezier(0.4, 0.0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0.0, 0.6, 1)',
      // Liquid Glass specific curves
      liquidEntry: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      liquidExit: 'cubic-bezier(0.55, 0.06, 0.68, 0.19)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },

    // Scale effects for interactions
    scale: {
      hover: 1.02,
      active: 0.98,
      focus: 1.01,
    },

    // Glow effects for focus states — blue accent, not purple
    glow: {
      intensity: {
        light: 0.15,
        dark: 0.2,
      },
      radius: '8px',
      color: {
        light: 'rgba(59, 130, 246, 0.3)',
        dark: 'rgba(59, 130, 246, 0.4)',
      },
    },
  },

  // Lensing effects for depth perception
  lensing: {
    // Border radius for glass elements
    borderRadius: {
      small: '8px',
      medium: '12px',
      large: '16px',
      xlarge: '20px',
    },

    // Refraction effects (using CSS filters)
    refraction: {
      subtle: 'brightness(1.05) contrast(1.02)',
      medium: 'brightness(1.1) contrast(1.05)',
      strong: 'brightness(1.15) contrast(1.08)',
    },

    // Edge highlighting for glass borders
    edgeHighlight: {
      light: 'rgba(255, 255, 255, 0.2)',
      dark: 'rgba(255, 255, 255, 0.1)',
    },
  },

  // Accessibility considerations
  accessibility: {
    // Minimum contrast ratios
    contrast: {
      minimum: 4.5,   // WCAG AA standard
      enhanced: 7,    // WCAG AAA standard
    },

    // Reduced motion alternatives
    reducedMotion: {
      duration: '0ms',
      scale: 1,
      blur: 0,
      transparency: 1,
    },

    // High contrast mode adjustments
    highContrast: {
      transparency: {
        regular: 1,
        clear: 1,
        prominent: 1,
        ultraThin: 1,
      },
      blur: 0,
      shadow: {
        light: '0 0 0 2px rgba(0, 0, 0, 0.8)',
        dark: '0 0 0 2px rgba(255, 255, 255, 0.8)',
      },
    },
  },
} as const

// Helper function to get theme-specific liquid glass values
export const getLiquidGlassValue = (
  path: string,
  theme: ColorTheme = 'light'
): any => {
  const pathArray = path.split('.')
  let current: any = liquidGlass
  
  for (const key of pathArray) {
    current = current[key]
  }
  
  if (typeof current === 'object' && current[theme] !== undefined) {
    return current[theme]
  }
  
  return current
}

// Create complete liquid glass style object
export const getLiquidGlassStyle = (
  variant: LiquidGlassVariant = 'regular',
  layer: LiquidGlassLayer = 'surface',
  theme: ColorTheme = 'light',
  motionPreference: MotionPreference = 'full'
) => {
  const transparency = getLiquidGlassValue(`transparency.${variant}`, theme)
  const blur = getLiquidGlassValue(`blur.${variant}`, theme)
  const depth = liquidGlass.depth[layer]
  const shadow = typeof depth.shadow === 'object' ? depth.shadow[theme] : depth.shadow

  // Apply accessibility preferences
  const finalTransparency = motionPreference === 'none' ? 1 : transparency
  const finalBlur = motionPreference === 'none' ? 0 : blur

  // Use appropriate background color for theme - matches design tokens
  // Dark: #000000 = rgb(0, 0, 0) - True black (flat, high contrast)
  // Light: #ffffff = rgb(255, 255, 255) - White
  const baseColor = theme === 'dark' ? '0, 0, 0' : '255, 255, 255'

  return {
    backgroundColor: `rgba(${baseColor}, ${finalTransparency})`,
    backdropFilter: `blur(${finalBlur}px)`,
    WebkitBackdropFilter: `blur(${finalBlur}px)`, // Safari support
    border: `1px solid ${liquidGlass.lensing.edgeHighlight[theme]}`,
    borderRadius: liquidGlass.lensing.borderRadius.medium,
    boxShadow: shadow,
    zIndex: depth.zIndex,
    position: 'relative' as const,
    overflow: 'hidden' as const,

    // Smooth transitions
    transition: motionPreference === 'none'
      ? 'none'
      : `all ${liquidGlass.motion.duration.normal} ${liquidGlass.motion.easing.standard}`,
  }
}

// Advanced liquid glass effect creator with interaction states
export const createLiquidGlassEffect = (
  variant: LiquidGlassVariant = 'regular',
  layer: LiquidGlassLayer = 'surface',
  theme: ColorTheme = 'light',
  motionPreference: MotionPreference = 'full',
  options?: {
    interactive?: boolean
    glowOnHover?: boolean
    scaleOnHover?: boolean
    customBorderRadius?: string
  }
) => {
  const baseStyle = getLiquidGlassStyle(variant, layer, theme, motionPreference)
  const { interactive = true, glowOnHover = true, scaleOnHover = true, customBorderRadius } = options || {}

  const interactiveStyles = interactive && motionPreference !== 'none' ? {
    cursor: 'pointer',

    '&:hover': {
      transform: scaleOnHover ? `scale(${liquidGlass.motion.scale.hover})` : 'none',
      boxShadow: glowOnHover
        ? `${baseStyle.boxShadow}, 0 0 ${liquidGlass.motion.glow.radius} ${liquidGlass.motion.glow.color[theme]}`
        : baseStyle.boxShadow,
      filter: liquidGlass.lensing.refraction.subtle,
    },

    '&:active': {
      transform: scaleOnHover ? `scale(${liquidGlass.motion.scale.active})` : 'none',
    },

    '&:focus-visible': {
      outline: 'none',
      transform: scaleOnHover ? `scale(${liquidGlass.motion.scale.focus})` : 'none',
      boxShadow: `${baseStyle.boxShadow}, 0 0 0 3px ${liquidGlass.motion.glow.color[theme]}`,
    },
  } : {}

  return {
    ...baseStyle,
    ...(customBorderRadius && { borderRadius: customBorderRadius }),
    ...interactiveStyles,
  }
}

// Utility function for responsive liquid glass based on screen size
export const getResponsiveLiquidGlass = (
  variant: LiquidGlassVariant = 'regular',
  layer: LiquidGlassLayer = 'surface',
  theme: ColorTheme = 'light',
  motionPreference: MotionPreference = 'full'
) => {
  const baseStyle = getLiquidGlassStyle(variant, layer, theme, motionPreference)

  return {
    ...baseStyle,

    // Reduce effects on smaller screens for performance
    '@media (max-width: 768px)': {
      backdropFilter: motionPreference === 'none' ? 'none' : `blur(${Math.max(getLiquidGlassValue(`blur.${variant}`, theme) - 4, 4)}px)`,
      WebkitBackdropFilter: motionPreference === 'none' ? 'none' : `blur(${Math.max(getLiquidGlassValue(`blur.${variant}`, theme) - 4, 4)}px)`,
    },

    // Enhanced effects on larger screens
    '@media (min-width: 1200px)': {
      backdropFilter: motionPreference === 'none' ? 'none' : `blur(${getLiquidGlassValue(`blur.${variant}`, theme) + 2}px)`,
      WebkitBackdropFilter: motionPreference === 'none' ? 'none' : `blur(${getLiquidGlassValue(`blur.${variant}`, theme) + 2}px)`,
    },
  }
}

// CSS-in-JS mixin for styled-components or emotion
export const liquidGlassMixin = (
  variant: LiquidGlassVariant = 'regular',
  layer: LiquidGlassLayer = 'surface',
  theme: ColorTheme = 'light',
  motionPreference: MotionPreference = 'full'
) => {
  const styles = getLiquidGlassStyle(variant, layer, theme, motionPreference)

  return Object.entries(styles)
    .map(([key, value]) => {
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase()
      return `${cssKey}: ${value};`
    })
    .join('\n  ')
}

export default liquidGlass
